import { useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { errMsg } from "../../../lib/xutil";
import { Field, btnGreen, chip, input } from "../../../components/common/Page";
import { SignaturePad } from "./SignaturePad";

const PhotoInput = ({ file, setFile, label, testId }) => (
  <label className="flex min-h-[88px] cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-white p-3 text-sm text-slate-500 hover:border-brand" data-testid={`${testId}-label`}>
    {file ? <img src={URL.createObjectURL(file)} alt="" className="h-16 w-16 rounded-lg object-cover" /> : <Camera className="h-6 w-6 text-navy" />}
    <span className="min-w-0 truncate">{file ? file.name : label}</span>
    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={testId} />
  </label>
);

export const ProofDialog = ({ stop, onClose, onDone }) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [photo, setPhoto] = useState(null);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (name.trim().length < 2 || !photo || !sig) { toast.error(t("x.route.pod.needAll")); return; }
    const fd = new FormData();
    fd.append("receiver_name", name.trim()); fd.append("photo", photo); fd.append("signature", sig, "signature.png");
    setBusy(true);
    try { await api.post(`/staff/deliveries/${stop.local_id}/pod`, fd); toast.success(t("x.route.pod.saved")); onDone(); } catch (e) { toast.error(errMsg(t, e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="pod-dialog">
        <DialogHeader><DialogTitle>{t("x.route.pod.title")} · {stop.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label={t("x.route.pod.receiver")}><input value={name} onChange={(e) => setName(e.target.value)} className={input} data-testid="pod-receiver" /></Field>
          <Field label={t("x.route.pod.photo")}><PhotoInput file={photo} setFile={setPhoto} label={t("x.route.pod.photo")} testId="pod-photo" /></Field>
          <Field label={t("x.route.pod.signature")}><SignaturePad onChange={setSig} clearLabel={t("x.route.pod.clear")} hint={t("x.route.pod.sign")} /></Field>
          <button type="button" disabled={busy} onClick={submit} className={`${btnGreen} w-full`} data-testid="pod-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("x.route.pod.submit")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const IssueDialog = ({ stop, onClose, onDone }) => {
  const { t, lang } = useI18n();
  const [type, setType] = useState("missing_item");
  const [item, setItem] = useState("");
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!photo) { toast.error(t("x.route.issueT.needPhoto")); return; }
    const fd = new FormData();
    fd.append("type", type); fd.append("note", note); if (item) fd.append("item_code", item); fd.append("photo", photo);
    setBusy(true);
    try { await api.post(`/staff/deliveries/${stop.local_id}/issues`, fd); toast.success(t("x.route.issueT.saved")); onDone(); } catch (e) { toast.error(errMsg(t, e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="issue-dialog">
        <DialogHeader><DialogTitle>{t("x.route.issueT.title")} · {stop.customer_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">{["missing_item", "damaged", "other"].map((k) => <button key={k} type="button" className={chip(type === k)} onClick={() => setType(k)} data-testid={`issue-type-${k}`}>{t(`x.route.issueT.${k}`)}</button>)}</div>
          <Field label={t("x.route.issueT.item")}>
            <select value={item} onChange={(e) => setItem(e.target.value)} className={input} data-testid="issue-item">
              <option value="">—</option>{stop.items.map((i) => <option key={i.item_code} value={i.item_code}>{i[`name_${lang}`]} ({i.qty_boxes})</option>)}
            </select>
          </Field>
          <Field label={t("x.route.issueT.note")}><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className={`${input} h-auto py-2`} data-testid="issue-note" /></Field>
          <Field label={t("x.route.issueT.photo")}><PhotoInput file={photo} setFile={setPhoto} label={t("x.route.issueT.photo")} testId="issue-photo" /></Field>
          <button type="button" disabled={busy} onClick={submit} className={`${btnGreen} w-full bg-rose-600 hover:bg-rose-700`} data-testid="issue-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("x.route.issueT.submit")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
